import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusColor = (status) => {
  switch (status) {
    case 'new': return 'bg-blue-100 text-blue-800';
    case 'contacted': return 'bg-yellow-100 text-yellow-800';
    case 'closed': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('preapprovals'); // default to pre-approvals so user sees missing leads
  const [contacts, setContacts] = useState([]);
  const [preApprovals, setPreApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchAll();
  }, []);

  const authHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [contactsRes, preApprovalsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: authHeader() }),
        axios.get(`${API}/admin/pre-approvals`, { headers: authHeader() }),
      ]);
      setContacts(contactsRes.data);
      setPreApprovals(preApprovalsRes.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch leads',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Contact handlers
  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeader() });
      setContacts(contacts.filter((c) => c.id !== id));
      toast({ title: 'Success', description: 'Contact deleted successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete contact', variant: 'destructive' });
    }
  };

  const handleContactStatusUpdate = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${id}/status?status=${newStatus}`,
        {},
        { headers: authHeader() }
      );
      setContacts(contacts.map((c) => (c.id === id ? { ...c, status: newStatus } : c)));
      toast({ title: 'Success', description: 'Status updated successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  // Pre-approval handlers
  const handleDeletePreApproval = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pre-approval lead?')) return;
    try {
      await axios.delete(`${API}/admin/pre-approvals/${id}`, { headers: authHeader() });
      setPreApprovals(preApprovals.filter((p) => p.id !== id));
      toast({ title: 'Success', description: 'Pre-approval deleted successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete pre-approval', variant: 'destructive' });
    }
  };

  const handlePreApprovalStatusUpdate = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/pre-approvals/${id}/status?status=${newStatus}`,
        {},
        { headers: authHeader() }
      );
      setPreApprovals(preApprovals.map((p) => (p.id === id ? { ...p, status: newStatus } : p)));
      toast({ title: 'Success', description: 'Status updated successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // CSV export helpers
  const downloadCsv = (filename, rows) => {
    if (!rows || rows.length === 0) {
      toast({ title: 'Nothing to export', description: 'No rows to download.' });
      return;
    }
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportContactsCsv = () => {
    const rows = contacts.map((c) => ({
      first_name: c.first_name,
      last_name: c.last_name,
      email: c.email,
      phone: c.phone,
      inquiry_notes: c.inquiry_notes || '',
      subscribe_to_mailing_list: c.subscribe_to_mailing_list ? 'Yes' : 'No',
      status: c.status,
      timestamp: new Date(c.timestamp).toISOString(),
    }));
    downloadCsv(`equipease-contacts-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const exportPreApprovalsCsv = () => {
    const rows = preApprovals.map((p) => ({
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      email: p.email || '',
      phone: p.phone || '',
      equipment_year: p.equipment_year,
      equipment_price: p.equipment_price,
      down_payment: p.down_payment,
      credit_score: p.credit_score,
      home_ownership: p.home_ownership,
      years_in_business: p.years_in_business || '',
      status: p.status,
      timestamp: new Date(p.timestamp).toISOString(),
    }));
    downloadCsv(`equipease-pre-approvals-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  // Filtering
  const filteredContacts = contacts.filter((contact) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      contact.first_name?.toLowerCase().includes(term) ||
      contact.last_name?.toLowerCase().includes(term) ||
      contact.email?.toLowerCase().includes(term) ||
      contact.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredPreApprovals = preApprovals.filter((p) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (p.first_name || '').toLowerCase().includes(term) ||
      (p.last_name || '').toLowerCase().includes(term) ||
      (p.email || '').toLowerCase().includes(term) ||
      (p.phone || '').includes(searchTerm) ||
      (p.credit_score || '').toLowerCase().includes(term);
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const currentList = activeTab === 'contacts' ? contacts : preApprovals;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">EquipEASE Admin</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.username}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchAll}
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="refresh-leads-btn"
              >
                Refresh
              </button>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                View Website
              </a>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                data-testid="logout-btn"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Pre-Approval Leads</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="stat-preapprovals-total">
              {preApprovals.length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Contact Inquiries</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="stat-contacts-total">
              {contacts.length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600 text-sm">New (All Types)</p>
            <p className="text-3xl font-bold text-blue-600">
              {contacts.filter((c) => c.status === 'new').length +
                preApprovals.filter((p) => p.status === 'new').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Closed (All Types)</p>
            <p className="text-3xl font-bold text-green-600">
              {contacts.filter((c) => c.status === 'closed').length +
                preApprovals.filter((p) => p.status === 'closed').length}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200 flex">
            <button
              onClick={() => {
                setActiveTab('preapprovals');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'preapprovals'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-preapprovals"
            >
              Pre-Approval Leads ({preApprovals.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('contacts');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'contacts'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-contacts"
            >
              Contact Inquiries ({contacts.length})
            </button>
          </div>

          {/* Filters */}
          <div className="p-6 grid md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <button
                onClick={activeTab === 'contacts' ? exportContactsCsv : exportPreApprovalsCsv}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                data-testid="export-csv-btn"
              >
                Export {activeTab === 'contacts' ? 'Contacts' : 'Pre-Approvals'} CSV ({currentList.length})
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {activeTab === 'preapprovals' ? (
          <PreApprovalsTable
            rows={filteredPreApprovals}
            onDelete={handleDeletePreApproval}
            onStatusUpdate={handlePreApprovalStatusUpdate}
          />
        ) : (
          <ContactsTable
            rows={filteredContacts}
            onDelete={handleDeleteContact}
            onStatusUpdate={handleContactStatusUpdate}
          />
        )}
      </main>
    </div>
  );
};

const ContactsTable = ({ rows, onDelete, onStatusUpdate }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="contacts-table">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mailing</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.length === 0 ? (
            <tr>
              <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                No contacts found
              </td>
            </tr>
          ) : (
            rows.map((contact) => (
              <tr key={contact.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {contact.first_name} {contact.last_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.phone}</td>
                <td className="px-6 py-4 max-w-xs">
                  <div className="text-sm text-gray-900 truncate" title={contact.inquiry_notes}>
                    {contact.inquiry_notes || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={contact.status}
                    onChange={(e) => onStatusUpdate(contact.id, e.target.value)}
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contact.status)}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {contact.subscribe_to_mailing_list ? 'Yes' : 'No'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(contact.timestamp).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => onDelete(contact.id)}
                    className="text-red-600 hover:text-red-900 font-medium"
                    data-testid={`delete-contact-${contact.id}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const PreApprovalsTable = ({ rows, onDelete, onStatusUpdate }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="preapprovals-table">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Down Payment</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Homeowner</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yrs in Biz</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.length === 0 ? (
            <tr>
              <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                No pre-approval leads found
              </td>
            </tr>
          ) : (
            rows.map((p) => {
              const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';
              return (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{fullName}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{p.email || '—'}</div>
                    <div className="text-xs text-gray-500">{p.phone || '—'}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{p.equipment_year || '—'}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(p.equipment_price)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(p.down_payment)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{p.credit_score}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{p.home_ownership}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{p.years_in_business || '—'}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <select
                      value={p.status}
                      onChange={(e) => onStatusUpdate(p.id, e.target.value)}
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(p.status)}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(p.timestamp).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => onDelete(p.id)}
                      className="text-red-600 hover:text-red-900 font-medium"
                      data-testid={`delete-preapproval-${p.id}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default AdminDashboard;
